/* eslint-disable react/no-array-index-key */
import { FC, useMemo, useState } from 'react';
import { useTranslate } from 'hooks/useTranslate';
import makeAnimated from 'react-select/animated';
import Select from 'react-select';

import { buildPagination } from '../utils/buildPagination';
import { useGetPageOptions } from '../hooks/useGetPageOptions';
import PaginationItem from './PaginationItem';
import { PageItem } from '../types/PageItem';

type TablePaginationProps = {
  totalRows: number;
  defaultPage?: number;
  defaultRowsPerPage?: number;
  showRowsPerPageOptions?: boolean;
  onChangePage: (page: number) => void; // eslint-disable-line
  onChangeRowsPerPage?: (rowsPerPage: number) => void; // eslint-disable-line
};

export const TablePagination: FC<TablePaginationProps> = ({
  totalRows,
  defaultPage = 1,
  defaultRowsPerPage = 10,
  showRowsPerPageOptions = true,
  onChangePage,
  onChangeRowsPerPage,
}) => {
  const { t } = useTranslate();
  const animatedComponents = makeAnimated();

  const [currentPage, setCurrentPage] = useState(defaultPage);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);

  const { data: pageData } = useGetPageOptions();

  const showPagination = totalRows > rowsPerPage;
  const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));

  const pagination = useMemo(
    () => buildPagination(totalRows, rowsPerPage, currentPage),
    [totalRows, rowsPerPage, currentPage]
  );

  const from = totalRows === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const to = Math.min(currentPage * rowsPerPage, totalRows);

  const handleChangeRowsPerPage = (newValue: any) => {
    const newRows = Number(newValue.value);

    setCurrentPage(1);
    setRowsPerPage(newRows);
    onChangePage(1);
    onChangeRowsPerPage?.(newRows);
  };

  const handleChangePage = (page: number) => {
    setCurrentPage(page);
    onChangePage(page);
  };

  return (
    <div className="row align-items-center mt-2">
      <div className="col-12 col-md-4 d-flex justify-content-center justify-content-md-start mb-2 mb-md-0">
        {showRowsPerPageOptions && (
          <Select
            id="rows-per-page"
            closeMenuOnSelect
            defaultValue={{ label: `${rowsPerPage}`, value: rowsPerPage }}
            className="react-select"
            options={pageData.pageOptions}
            components={animatedComponents}
            onChange={handleChangeRowsPerPage}
          />
        )}
      </div>

      <div className="col-12 col-md-4 d-flex justify-content-center mb-2 mb-md-0">
        <nav aria-label="pagination">
          {showPagination && (
            <ul className="pagination mb-0">
              {pagination.map((item: PageItem, index: number) => (
                <PaginationItem
                  key={`${item}-${index}`}
                  item={item}
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onChange={handleChangePage}
                />
              ))}
            </ul>
          )}
        </nav>
      </div>

      <div className="col-12 col-md-4 d-flex justify-content-center justify-content-md-end">
        <div className="dt-info" aria-live="polite">
          {from} - {to} {t('of')} {totalRows}
        </div>
      </div>
    </div>
  );
};
